import { Component, Input, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import {
  EventClassResponse,
  EventScheduleResponse,
  EventScheduleEntryResponse,
  EventScheduleGroupResponse
} from '../../../../../../../../../../@shared/models/response.module';
import {
  _AQH_EVE_42,
  _AQH_EVE_45,
  _AQH_EVE_46,
  _AQH_EVE_47,
  _AQH_EVE_48,
  _AQH_EVE_49,
  _AQH_EVE_50,
  _AQH_EVE_73,
  _AQH_EVE_74,
  AQH_EVE_44
} from '../../../../../../../../../../@shared/models/input.module';

interface ScheduleGroup {
  schedule_group_id: number; // API group ID (0 for new unsaved groups)
  local_id: number; // Local tracking ID
  name: string;
  start_time: string; // HH:mm format
  duration_minutes: number;
  entries: EventScheduleEntryResponse[];
  isNew?: boolean; // Track if this group needs to be created
  isDeleted?: boolean; // Track if this group needs to be deleted
}

interface ScheduleBlock {
  type: 'group' | 'break';
  group?: ScheduleGroup;
  break_duration_minutes?: number;
  break_notes?: string;
  entry?: EventScheduleEntryResponse; // For break entries
  previousGroupLocalId?: number; // For breaks - tracks which group they follow (by local_id)
}

@Component({
  selector: 'app-schedule-editor',
  standalone: false,
  templateUrl: './schedule-editor.html',
  styleUrl: './schedule-editor.css'
})
export class ScheduleEditor {
  @Input() schedule: EventScheduleResponse;
  @Input() eventClass: EventClassResponse;

  entries: EventScheduleEntryResponse[] = [];
  loading = true;
  saving = false;
  hasChanges = false;
  showUnsavedWarning = false;

  // For clinic day schedule
  groups: ScheduleGroup[] = [];
  scheduleBlocks: ScheduleBlock[] = [];
  allParticipants: EventScheduleEntryResponse[] = []; // Master list of all participants
  unassignedParticipants: EventScheduleEntryResponse[] = []; // Participants not in any group
  nextLocalId = 1;
  deletedGroupIds: number[] = []; // Track groups to delete on save
  deletedEntryIds: number[] = []; // Track entries to delete on save

  // Settings
  scheduleName: string = '';
  advancementMode: string = 'manual';
  timePerEntry: number = 120;
  sessionDuration: number = 3600;
  scheduleStartTime: string = '09:00'; // HH:mm format for time calculations
  scheduleStartDateTime: string = ''; // YYYY-MM-DDTHH:mm format for datetime-local input
  scheduleStartDate: Date = new Date(); // Full date object

  // Drag state (for clinic groups)
  draggedEntry: EventScheduleEntryResponse | null = null;
  draggedFromGroup: number | null = null;

  // Drag state (for start list)
  draggedStartListEntry: EventScheduleEntryResponse | null = null;
  draggedStartListIndex: number = -1;
  startListDragOverIndex: number = -1;

  // Auto-scroll
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;
  private autoScrollInterval: any = null;
  private readonly SCROLL_SPEED = 15;
  private readonly SCROLL_ZONE = 200; // pixels from edge to trigger scroll

  // Duration presets
  durationPresets = [
    { label: '30 min', value: 30 },
    { label: '45 min', value: 45 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 }
  ];

  breakPresets = [
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 }
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private eventService: EventsService
  ) {}

  ngOnInit() {
    this.scheduleName = this.schedule.schedule_name || '';
    this.advancementMode = this.schedule.advancement_mode;
    this.timePerEntry = this.schedule.time_per_entry_seconds;
    this.sessionDuration = this.schedule.time_per_entry_seconds;

    if (this.schedule.schedule_start_time) {
      const date = new Date(this.schedule.schedule_start_time);
      this.scheduleStartDate = date;
      this.scheduleStartTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
      // Format for datetime-local input: YYYY-MM-DDTHH:mm
      this.scheduleStartDateTime = this.formatDateTimeLocal(date);
    } else {
      // Default to tomorrow at 09:00 if no start time set
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      this.scheduleStartDate = tomorrow;
      this.scheduleStartTime = '09:00';
      this.scheduleStartDateTime = this.formatDateTimeLocal(tomorrow);
    }

    this.loadEntries();
  }

  // Format Date to datetime-local format (YYYY-MM-DDTHH:mm)
  formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  get isClinic(): boolean {
    return this.schedule.schedule_type === 'clinic';
  }

  get isGroupSession(): boolean {
    return this.schedule.session_type === 'group';
  }

  loadEntries() {
    const params: AQH_EVE_44 = {
      schedule_id: this.schedule.schedule_id
    };
    this.eventService.getScheduleEntries(params).subscribe({
      next: (entries) => {
        this.entries = entries.sort((a, b) => a.position - b.position);
        if (this.isClinic) {
          this.organizeEntriesIntoGroups();
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  organizeEntriesIntoGroups() {
    this.groups = [];
    this.allParticipants = [];
    this.unassignedParticipants = [];
    this.scheduleBlocks = [];

    // Build master participant list from entries (excluding breaks)
    for (const entry of this.entries) {
      if (entry.entry_type === 'break') continue;
      if (!this.allParticipants.find(p => p.class_registration_id === entry.class_registration_id)) {
        this.allParticipants.push(entry);
      }
    }

    // Load groups from the schedule's groups array (new structure)
    if (this.schedule.groups && this.schedule.groups.length > 0) {
      for (const apiGroup of this.schedule.groups) {
        let startTime = this.scheduleStartTime;
        if (apiGroup.start_time) {
          const date = new Date(apiGroup.start_time);
          startTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        }

        // Find entries assigned to this group
        const groupEntries = this.entries.filter(e =>
          e.schedule_group_id === apiGroup.schedule_group_id && e.entry_type !== 'break'
        );

        const group: ScheduleGroup = {
          schedule_group_id: apiGroup.schedule_group_id,
          local_id: this.nextLocalId++,
          name: apiGroup.group_name,
          start_time: startTime,
          duration_minutes: Math.floor((apiGroup.duration_seconds || this.sessionDuration) / 60),
          entries: groupEntries.sort((a, b) => a.position - b.position),
          isNew: false
        };
        this.groups.push(group);
      }
    } else {
      // Fallback: try to build groups from legacy group_id on entries
      const groupMap = new Map<number, EventScheduleEntryResponse[]>();
      for (const entry of this.entries) {
        if (entry.entry_type === 'break') continue;
        if (entry.group_id) {
          if (!groupMap.has(entry.group_id)) {
            groupMap.set(entry.group_id, []);
          }
          groupMap.get(entry.group_id)!.push(entry);
        }
      }

      groupMap.forEach((entries, groupId) => {
        const firstEntry = entries[0];
        let startTime = this.scheduleStartTime;
        if (firstEntry.start_time) {
          const date = new Date(firstEntry.start_time);
          startTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        }

        const group: ScheduleGroup = {
          schedule_group_id: 0, // No API group yet
          local_id: this.nextLocalId++,
          name: firstEntry.lesson_name || `Group ${groupId}`,
          start_time: startTime,
          duration_minutes: Math.floor((firstEntry.duration_seconds || this.sessionDuration) / 60),
          entries: entries.sort((a, b) => a.position - b.position),
          isNew: true // Will need to create via API
        };
        this.groups.push(group);
      });
    }

    // Find unassigned participants (not in any group)
    const assignedRegistrationIds = new Set<number>();
    for (const group of this.groups) {
      for (const entry of group.entries) {
        if (entry.class_registration_id) {
          assignedRegistrationIds.add(entry.class_registration_id);
        }
      }
    }

    this.unassignedParticipants = this.allParticipants.filter(p =>
      p.class_registration_id && !assignedRegistrationIds.has(p.class_registration_id)
    );

    // Sort groups by start time
    this.groups.sort((a, b) => a.start_time.localeCompare(b.start_time));

    // Build schedule blocks (groups + breaks in order)
    this.rebuildScheduleBlocks();
  }

  rebuildScheduleBlocks() {
    this.scheduleBlocks = [];

    // Sort groups by position/start time
    const sortedGroups = [...this.groups].sort((a, b) => a.start_time.localeCompare(b.start_time));

    for (let i = 0; i < sortedGroups.length; i++) {
      const group = sortedGroups[i];

      // Check if we need a break before this group
      if (i > 0) {
        const prevGroup = sortedGroups[i - 1];
        const prevEndTime = this.addMinutesToTime(prevGroup.start_time, prevGroup.duration_minutes);
        const gapMinutes = this.getMinutesBetweenTimes(prevEndTime, group.start_time);

        if (gapMinutes > 0) {
          // Find break entry if exists
          const prevEntryPositions = prevGroup.entries.map(x => x.position);
          const groupEntryPositions = group.entries.map(x => x.position);
          const breakEntry = this.entries.find(e =>
            e.entry_type === 'break' &&
            (prevEntryPositions.length === 0 || e.position > Math.max(...prevEntryPositions)) &&
            (groupEntryPositions.length === 0 || e.position < Math.min(...groupEntryPositions))
          );

          this.scheduleBlocks.push({
            type: 'break',
            break_duration_minutes: gapMinutes,
            break_notes: breakEntry?.notes || 'Break',
            entry: breakEntry,
            previousGroupLocalId: prevGroup.local_id
          });
        }
      }

      this.scheduleBlocks.push({
        type: 'group',
        group: group
      });
    }
  }

  // Group management
  addGroup() {
    const lastGroup = this.groups[this.groups.length - 1];
    let startTime = this.scheduleStartTime;

    if (lastGroup) {
      // New groups start immediately after the previous one (no break by default)
      startTime = this.addMinutesToTime(lastGroup.start_time, lastGroup.duration_minutes);
    }

    const newGroup: ScheduleGroup = {
      schedule_group_id: 0, // Will be assigned by API when saved
      local_id: this.nextLocalId++,
      name: `Group ${this.groups.length + 1}`,
      start_time: startTime,
      duration_minutes: 45,
      entries: [],
      isNew: true
    };

    this.groups.push(newGroup);
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  removeGroup(group: ScheduleGroup) {
    // Track groups to delete (only if they exist in API)
    if (group.schedule_group_id > 0) {
      this.deletedGroupIds.push(group.schedule_group_id);
    }
    // Move entries back to unassigned
    for (const entry of group.entries) {
      if (!this.unassignedParticipants.find(p => p.class_registration_id === entry.class_registration_id)) {
        this.unassignedParticipants.push(entry);
      }
    }
    this.groups = this.groups.filter(g => g.local_id !== group.local_id);
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  updateGroupName(group: ScheduleGroup, name: string) {
    group.name = name;
    this.hasChanges = true;
  }

  updateGroupStartTime(group: ScheduleGroup, time: string) {
    group.start_time = time;
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  updateGroupDuration(group: ScheduleGroup, minutes: number) {
    group.duration_minutes = minutes;
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  // Clinic settings change handlers
  onClinicSettingsChange() {
    this.hasChanges = true;
  }

  onScheduleStartDateTimeChange(newDateTime: string) {
    if (!newDateTime) return;

    // Parse the datetime-local value
    const newDate = new Date(newDateTime);
    const newTime = `${newDate.getHours().toString().padStart(2, '0')}:${newDate.getMinutes().toString().padStart(2, '0')}`;

    // Update the date
    this.scheduleStartDate = newDate;

    if (this.groups.length > 0) {
      // Calculate the time difference
      const oldStartTime = this.groups[0].start_time;
      const diffMinutes = this.getMinutesBetweenTimes(oldStartTime, newTime);

      // Shift all groups by the difference
      for (const group of this.groups) {
        group.start_time = this.addMinutesToTime(group.start_time, diffMinutes);
      }

      this.rebuildScheduleBlocks();
    }

    this.scheduleStartTime = newTime;
    this.hasChanges = true;
  }

  // Add break between groups
  addBreakAfterGroup(group: ScheduleGroup, durationMinutes: number = 15) {
    const groupIndex = this.groups.findIndex(g => g.local_id === group.local_id);
    if (groupIndex < this.groups.length - 1) {
      const nextGroup = this.groups[groupIndex + 1];
      const newStartTime = this.addMinutesToTime(group.start_time, group.duration_minutes + durationMinutes);
      nextGroup.start_time = newStartTime;

      // Shift all subsequent groups by the same amount
      for (let i = groupIndex + 2; i < this.groups.length; i++) {
        const prevGroup = this.groups[i - 1];
        const currentGap = this.getMinutesBetweenTimes(
          this.addMinutesToTime(prevGroup.start_time, prevGroup.duration_minutes),
          this.groups[i].start_time
        );
        // Preserve existing gaps for subsequent groups
        this.groups[i].start_time = this.addMinutesToTime(
          prevGroup.start_time,
          prevGroup.duration_minutes + Math.max(0, currentGap)
        );
      }
    }
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  // Remove break between groups (make them back-to-back)
  removeBreakBetweenGroups(previousGroupLocalId: number) {
    const groupIndex = this.groups.findIndex(g => g.local_id === previousGroupLocalId);
    if (groupIndex >= 0 && groupIndex < this.groups.length - 1) {
      const currentGroup = this.groups[groupIndex];
      const nextGroup = this.groups[groupIndex + 1];

      // Calculate the break duration being removed
      const currentEndTime = this.addMinutesToTime(currentGroup.start_time, currentGroup.duration_minutes);
      const breakDuration = this.getMinutesBetweenTimes(currentEndTime, nextGroup.start_time);

      // Set next group to start immediately after current group
      nextGroup.start_time = currentEndTime;

      // Shift all subsequent groups by the same amount
      for (let i = groupIndex + 2; i < this.groups.length; i++) {
        const prevGroup = this.groups[i - 1];
        const currentGap = this.getMinutesBetweenTimes(
          this.addMinutesToTime(prevGroup.start_time, prevGroup.duration_minutes),
          this.groups[i].start_time
        );
        this.groups[i].start_time = this.addMinutesToTime(
          prevGroup.start_time,
          prevGroup.duration_minutes + Math.max(0, currentGap - breakDuration)
        );
      }
    }
    this.rebuildScheduleBlocks();
    this.hasChanges = true;
  }

  // Drag and drop between groups
  onEntryDragStart(event: DragEvent, entry: EventScheduleEntryResponse, fromGroupLocalId: number | null) {
    this.draggedEntry = entry;
    this.draggedFromGroup = fromGroupLocalId;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', entry.entry_id.toString());
    }
  }

  onGroupDragOver(event: DragEvent) {
    if (this.draggedEntry) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    }
  }

  onGroupDrop(event: DragEvent, toGroupLocalId: number | null) {
    event.preventDefault();
    if (!this.draggedEntry || toGroupLocalId === null) return;

    const targetGroup = this.groups.find(g => g.local_id === toGroupLocalId);
    if (!targetGroup) return;

    // Check if already in this group
    if (this.isInGroup(this.draggedEntry, toGroupLocalId)) {
      this.draggedEntry = null;
      this.draggedFromGroup = null;
      return;
    }

    // Dragging from "All Participants" list (-1) or from another group - COPY to new group
    if (this.draggedFromGroup === -1 || (this.draggedFromGroup !== null && this.draggedFromGroup !== toGroupLocalId)) {
      // Create a copy of the entry for the new group
      const entryCopy: EventScheduleEntryResponse = {
        ...this.draggedEntry,
        entry_id: Date.now(), // Temporary ID for new entry
        schedule_group_id: targetGroup.schedule_group_id
      };
      targetGroup.entries.push(entryCopy);

      // If dragging from "All Participants" and participant was unassigned, remove from unassigned
      if (this.draggedFromGroup === -1) {
        this.unassignedParticipants = this.unassignedParticipants.filter(
          p => p.class_registration_id !== this.draggedEntry!.class_registration_id
        );
      }
    } else {
      // Dragging from unassigned list (null) - add original to this group
      this.draggedEntry.schedule_group_id = targetGroup.schedule_group_id;
      targetGroup.entries.push(this.draggedEntry);
      // Remove from unassigned
      this.unassignedParticipants = this.unassignedParticipants.filter(
        p => p.class_registration_id !== this.draggedEntry!.class_registration_id
      );
    }

    this.hasChanges = true;
    this.draggedEntry = null;
    this.draggedFromGroup = null;
  }

  onDragEnd() {
    this.draggedEntry = null;
    this.draggedFromGroup = null;
    this.stopAutoScroll();
  }

  // Auto-scroll when dragging near edges
  onContainerDragOver(event: DragEvent) {
    if (!this.draggedEntry || !this.scrollContainer) return;

    event.preventDefault();

    const container = this.scrollContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const mouseY = event.clientY;

    // Calculate distance from edges
    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    // Scroll up when in top zone
    if (distanceFromTop < this.SCROLL_ZONE && distanceFromTop >= 0) {
      // Faster scroll the closer to edge
      const speed = this.SCROLL_SPEED * (1 - distanceFromTop / this.SCROLL_ZONE);
      this.startAutoScroll(-Math.max(speed, 4));
    }
    // Scroll down when in bottom zone
    else if (distanceFromBottom < this.SCROLL_ZONE && distanceFromBottom >= 0) {
      // Faster scroll the closer to edge
      const speed = this.SCROLL_SPEED * (1 - distanceFromBottom / this.SCROLL_ZONE);
      this.startAutoScroll(Math.max(speed, 4));
    }
    // Not near edges, stop scrolling
    else {
      this.stopAutoScroll();
    }
  }

  private startAutoScroll(speed: number) {
    if (this.autoScrollInterval) return;

    this.autoScrollInterval = setInterval(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop += speed;
      }
    }, 16); // ~60fps
  }

  private stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  // Check if participant is in a specific group (by local_id)
  isInGroup(entry: EventScheduleEntryResponse, groupLocalId: number): boolean {
    const group = this.groups.find(g => g.local_id === groupLocalId);
    if (!group) return false;
    return group.entries.some(e => e.class_registration_id === entry.class_registration_id);
  }

  // Get all groups a participant is in
  getParticipantGroups(entry: EventScheduleEntryResponse): ScheduleGroup[] {
    return this.groups.filter(g =>
      g.entries.some(e => e.class_registration_id === entry.class_registration_id)
    );
  }

  // Remove participant from a specific group (by local_id)
  removeFromGroup(entry: EventScheduleEntryResponse, groupLocalId: number) {
    const group = this.groups.find(g => g.local_id === groupLocalId);
    if (group) {
      group.entries = group.entries.filter(e => e.entry_id !== entry.entry_id);
      // Add back to unassigned if not in any other group
      const stillAssigned = this.groups.some(g =>
        g.local_id !== groupLocalId &&
        g.entries.some(e => e.class_registration_id === entry.class_registration_id)
      );
      if (!stillAssigned && !this.unassignedParticipants.find(p => p.class_registration_id === entry.class_registration_id)) {
        this.unassignedParticipants.push(entry);
      }
      this.hasChanges = true;
    }
  }

  // Get count of groups participant is in
  getGroupCount(entry: EventScheduleEntryResponse): number {
    return this.getParticipantGroups(entry).length;
  }

  // Save all changes
  async saveClinicSchedule() {
    this.saving = true;

    try {
      console.log('=== Saving Clinic Schedule ===');
      console.log('Groups:', this.groups.length);
      console.log('Unassigned:', this.unassignedParticipants.length);
      console.log('Schedule blocks:', this.scheduleBlocks.length);

      // Update schedule settings
      console.log('Step 0: Updating schedule settings');
      await firstValueFrom(this.eventService.updateSchedule({
        schedule_id: this.schedule.schedule_id,
        schedule_name: this.scheduleName,
        time_per_entry_seconds: this.groups[0]?.duration_minutes * 60 || 2700,
        schedule_start_time: this.parseTimeToDate(this.scheduleStartTime).toISOString()
      }));

      // 1. Delete groups that were removed
      console.log('Step 1: Deleting removed groups:', this.deletedGroupIds.length);
      for (const groupId of this.deletedGroupIds) {
        await firstValueFrom(this.eventService.deleteScheduleGroup({
          schedule_group_id: groupId
        }));
      }
      this.deletedGroupIds = [];

      // 2. Create new groups and update existing groups
      console.log('Step 2: Creating/updating groups');
      let groupPosition = 1;
      for (const group of this.groups) {
        const groupStartTime = this.parseTimeToDate(group.start_time);
        const groupEndTime = new Date(groupStartTime.getTime() + group.duration_minutes * 60000);

        if (group.isNew) {
          console.log('Creating new group:', group.name);
          // Create new group
          const newGroup = await firstValueFrom(this.eventService.createScheduleGroup({
            schedule_id: this.schedule.schedule_id,
            group_name: group.name,
            position: groupPosition++,
            start_time: groupStartTime.toISOString(),
            end_time: groupEndTime.toISOString(),
            duration_seconds: group.duration_minutes * 60
          }));
          // Update local reference with API ID
          if (newGroup) {
            group.schedule_group_id = newGroup.schedule_group_id;
            group.isNew = false;
          }
        } else if (group.schedule_group_id > 0) {
          console.log('Updating existing group:', group.name, 'id:', group.schedule_group_id);
          // Update existing group
          await firstValueFrom(this.eventService.updateScheduleGroup({
            schedule_group_id: group.schedule_group_id,
            group_name: group.name,
            position: groupPosition++,
            start_time: groupStartTime.toISOString(),
            end_time: groupEndTime.toISOString(),
            duration_seconds: group.duration_minutes * 60
          }));
        }
      }

      // 3. Update entries with their group assignments (only if there are entries)
      console.log('Step 3: Updating group entries');
      let entryPosition = 1;
      for (const group of this.groups) {
        for (const entry of group.entries) {
          // Check if this is a new entry (copied participant to another group)
          const isNewEntry = entry.entry_id > 1000000000000;

          if (isNewEntry && entry.class_registration_id) {
            console.log('Creating new entry for participant in group:', group.name);
            // Create a new entry for this participant in this group
            const newEntry = await firstValueFrom(this.eventService.addScheduleEntry({
              schedule_id: this.schedule.schedule_id,
              class_registration_id: entry.class_registration_id,
              position: entryPosition++,
              entry_type: 'participant',
              schedule_group_id: group.schedule_group_id,
              lesson_name: group.name
            }));
            if (newEntry) {
              entry.entry_id = newEntry.entry_id;
            }
          } else {
            console.log('Updating entry:', entry.entry_id, 'for group:', group.name);
            // Update entry with schedule_group_id assignment
            await firstValueFrom(this.eventService.updateScheduleEntry({
              entry_id: entry.entry_id,
              position: entryPosition++,
              schedule_group_id: group.schedule_group_id,
              lesson_name: group.name
            }));
          }
        }
      }

      // 4. Clear group assignment for unassigned participants
      console.log('Step 4: Clearing unassigned participants:', this.unassignedParticipants.length);
      for (const entry of this.unassignedParticipants) {
        console.log('Clearing entry:', entry.entry_id);
        await firstValueFrom(this.eventService.updateScheduleEntry({
          entry_id: entry.entry_id,
          position: entryPosition++,
          schedule_group_id: null  // Use null to clear FK, not 0
        }));
      }

      // 5. Handle breaks from scheduleBlocks
      console.log('Step 5: Handling breaks');
      for (const block of this.scheduleBlocks) {
        if (block.type === 'break') {
          if (block.entry && block.entry.entry_id) {
            console.log('Updating existing break:', block.entry.entry_id);
            // Update existing break
            await firstValueFrom(this.eventService.updateScheduleEntry({
              entry_id: block.entry.entry_id,
              position: entryPosition++,
              duration_seconds: (block.break_duration_minutes || 15) * 60,
              notes: block.break_notes
            }));
          } else {
            console.log('Creating new break');
            // Create new break entry
            await firstValueFrom(this.eventService.addScheduleEntry({
              schedule_id: this.schedule.schedule_id,
              position: entryPosition++,
              entry_type: 'break',
              duration_seconds: (block.break_duration_minutes || 15) * 60,
              notes: block.break_notes
            }));
          }
        }
      }

      console.log('=== Save completed successfully ===');
      this.hasChanges = false;
      this.activeModal.close(true);
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      console.error('Error details:', error?.error || error?.message || error);
    } finally {
      this.saving = false;
    }
  }

  // Time helpers
  addMinutesToTime(time: string, minutes: number): string {
    const [hours, mins] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  }

  getMinutesBetweenTimes(start: string, end: string): number {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    return (endH * 60 + endM) - (startH * 60 + startM);
  }

  parseTimeToDate(time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    // Use the scheduleStartDate to preserve the correct day
    const date = new Date(this.scheduleStartDate);
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  // Parse datetime-local string to Date
  parseDateTimeLocalToDate(dateTimeLocal: string): Date {
    return new Date(dateTimeLocal);
  }

  getGroupEndTime(group: ScheduleGroup): string {
    return this.addMinutesToTime(group.start_time, group.duration_minutes);
  }

  formatDuration(minutes: number): string {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    return `${minutes} min`;
  }

  // Entry helpers
  getParticipantName(entry: EventScheduleEntryResponse): string {
    if (!entry.registration) return 'Unknown';
    const reg = entry.registration;
    return reg.user?.full_name || `${reg.user?.first_name || ''} ${reg.user?.last_name || ''}`.trim();
  }

  getHorseName(entry: EventScheduleEntryResponse): string {
    return entry.registration?.horse?.name || '';
  }

  getActiveParticipantCount(): number {
    return this.entries.filter(e => e.status !== 'scratched' && e.entry_type === 'participant').length;
  }

  // Standard schedule methods (for non-clinic)
  saveSettings() {
    const params: _AQH_EVE_42 = {
      schedule_id: this.schedule.schedule_id,
      schedule_name: this.scheduleName,
      advancement_mode: this.advancementMode,
      time_per_entry_seconds: this.isGroupSession ? this.sessionDuration : this.timePerEntry
    };

    this.saving = true;
    this.eventService.updateSchedule(params).subscribe({
      next: (updated) => {
        this.schedule = updated;
        this.hasChanges = true;
        this.saving = false;
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  startLive() {
    const params: _AQH_EVE_49 = {
      schedule_id: this.schedule.schedule_id
    };

    this.saving = true;
    this.eventService.startLiveSchedule(params).subscribe({
      next: (updatedSchedule) => {
        this.schedule = updatedSchedule;
        this.hasChanges = true;
        this.saving = false;
      },
      error: () => {
        this.saving = false;
      }
    });
  }

  close() {
    if (this.hasChanges) {
      this.showUnsavedWarning = true;
    } else {
      this.activeModal.close(false);
    }
  }

  discardAndClose() {
    this.showUnsavedWarning = false;
    this.activeModal.close(false);
  }

  async saveAndClose() {
    this.showUnsavedWarning = false;

    if (this.isClinic) {
      // Use the unified save method for clinics
      await this.saveClinicSchedule();
      return;
    }

    // Use the start list save method for non-clinic schedules
    await this.saveStartList();
  }

  cancelClose() {
    this.showUnsavedWarning = false;
  }

  // ============================================
  // START LIST METHODS
  // ============================================

  onStartListSettingsChange() {
    this.hasChanges = true;
  }

  get timePerEntryMinutes(): number {
    return (this.timePerEntry || 0) / 60;
  }

  updateTimePerEntryMinutes(minutes: number) {
    const parsed = typeof minutes === 'number' ? minutes : parseFloat(minutes as any);
    const safeMinutes = isNaN(parsed) || parsed < 0.5 ? 0.5 : parsed;
    this.timePerEntry = Math.round(safeMinutes * 60);
    this.onStartListSettingsChange();
  }

  onStartListDateTimeChange(newDateTime: string) {
    if (!newDateTime) return;

    // Parse the datetime-local value
    const newDate = new Date(newDateTime);
    const newTime = `${newDate.getHours().toString().padStart(2, '0')}:${newDate.getMinutes().toString().padStart(2, '0')}`;

    // Update the date and time
    this.scheduleStartDate = newDate;
    this.scheduleStartTime = newTime;
    this.hasChanges = true;
  }

  // Calculate estimated time for an entry based on position and previous entries
  getEntryEstimatedTime(entry: EventScheduleEntryResponse, index: number): string {
    let totalSeconds = 0;

    // Start from schedule start time
    const [startHours, startMins] = this.scheduleStartTime.split(':').map(Number);
    const baseTime = startHours * 3600 + startMins * 60;

    // Add up time for all previous entries
    for (let i = 0; i < index; i++) {
      const prevEntry = this.entries[i];
      if (prevEntry.entry_type === 'break') {
        totalSeconds += prevEntry.duration_seconds || 300; // default 5 min break
      } else {
        totalSeconds += this.timePerEntry;
      }
    }

    const entryTimeSeconds = baseTime + totalSeconds;
    const hours = Math.floor(entryTimeSeconds / 3600) % 24;
    const mins = Math.floor((entryTimeSeconds % 3600) / 60);

    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Drag and drop for start list reordering
  onStartListDragStart(event: DragEvent, entry: EventScheduleEntryResponse, index: number) {
    this.draggedStartListEntry = entry;
    this.draggedStartListIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', entry.entry_id.toString());
    }
  }

  onStartListDragOver(event: DragEvent, index: number) {
    if (this.draggedStartListEntry && this.draggedStartListIndex !== index) {
      event.preventDefault();
      this.startListDragOverIndex = index;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    }
  }

  onStartListDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    if (!this.draggedStartListEntry || this.draggedStartListIndex === dropIndex) {
      this.startListDragOverIndex = -1;
      return;
    }

    // Remove from old position
    const entry = this.entries.splice(this.draggedStartListIndex, 1)[0];

    // Adjust drop index if needed (if we removed from before the drop point)
    const adjustedIndex = this.draggedStartListIndex < dropIndex ? dropIndex - 1 : dropIndex;

    // Insert at new position
    this.entries.splice(adjustedIndex, 0, entry);

    // Recalculate positions
    this.recalculatePositions();

    this.hasChanges = true;
    this.startListDragOverIndex = -1;
    this.draggedStartListEntry = null;
    this.draggedStartListIndex = -1;
  }

  onStartListDragEnd() {
    this.draggedStartListEntry = null;
    this.draggedStartListIndex = -1;
    this.startListDragOverIndex = -1;
  }

  recalculatePositions() {
    let position = 1;
    for (const entry of this.entries) {
      // All entries (including breaks) get sequential positions to maintain order
      entry.position = position++;
    }
  }

  // Add break after an entry
  addStartListBreak(afterIndex: number, durationSeconds: number, notes: string) {
    const breakEntry: EventScheduleEntryResponse = {
      entry_id: Date.now(), // Temporary ID
      schedule_id: this.schedule.schedule_id,
      position: 0, // Will be calculated in recalculatePositions
      entry_type: 'break',
      duration_seconds: durationSeconds,
      notes: notes,
      status: 'pending'
    } as EventScheduleEntryResponse;

    // Insert after the specified index
    this.entries.splice(afterIndex + 1, 0, breakEntry);
    // Recalculate all positions to maintain order
    this.recalculatePositions();
    this.hasChanges = true;
  }

  removeStartListBreak(breakEntry: EventScheduleEntryResponse) {
    // Track for deletion if it's an existing entry (not a new temp ID)
    if (breakEntry.entry_id <= 1000000000000) {
      this.deletedEntryIds.push(breakEntry.entry_id);
    }
    this.entries = this.entries.filter(e => e.entry_id !== breakEntry.entry_id);
    // Recalculate positions to maintain order
    this.recalculatePositions();
    this.hasChanges = true;
  }

  updateBreakNotes(entry: EventScheduleEntryResponse, notes: string) {
    entry.notes = notes;
    this.hasChanges = true;
  }

  updateBreakDuration(entry: EventScheduleEntryResponse, duration: number) {
    entry.duration_seconds = duration;
    this.hasChanges = true;
  }

  updateBreakDurationMinutes(entry: EventScheduleEntryResponse, minutes: number) {
    const parsed = typeof minutes === 'number' ? minutes : parseFloat(minutes as any);
    const safeMinutes = isNaN(parsed) || parsed < 1 ? 1 : parsed;
    entry.duration_seconds = Math.round(safeMinutes * 60);
    this.hasChanges = true;
  }

  // Save start list changes
  async saveStartList() {
    this.saving = true;

    try {
      // Ensure all entries have correct sequential positions before saving
      this.recalculatePositions();

      // Update schedule settings
      await firstValueFrom(this.eventService.updateSchedule({
        schedule_id: this.schedule.schedule_id,
        schedule_name: this.scheduleName,
        advancement_mode: this.advancementMode,
        time_per_entry_seconds: this.timePerEntry,
        schedule_start_time: this.parseTimeToDate(this.scheduleStartTime).toISOString()
      }));

      // Delete removed entries first
      for (const entryId of this.deletedEntryIds) {
        await firstValueFrom(this.eventService.deleteScheduleEntry({
          entry_id: entryId
        }));
      }
      this.deletedEntryIds = [];

      // Separate new entries from existing ones
      const newEntries = this.entries.filter(e => e.entry_id > 1000000000000);

      // Create new entries WITHOUT position (they get added at the end)
      // This avoids the backend auto-shifting existing entries
      for (const entry of newEntries) {
        // Participant entries always follow the schedule-wide timePerEntry;
        // breaks keep their own explicit duration.
        const duration = entry.entry_type === 'break'
          ? (entry.duration_seconds || this.timePerEntry)
          : this.timePerEntry;
        const newEntry = await firstValueFrom(this.eventService.addScheduleEntry({
          schedule_id: this.schedule.schedule_id,
          entry_type: entry.entry_type,
          duration_seconds: duration,
          notes: entry.notes || undefined
        }));
        if (newEntry) {
          entry.entry_id = newEntry.entry_id;
        }
      }

      // Batch update ALL entries (including newly created ones) with correct positions
      // This ensures proper ordering and recalculates scheduled times
      const batchData = this.entries.map(entry => ({
        entry_id: entry.entry_id,
        position: entry.position,
        duration_seconds: entry.entry_type === 'break'
          ? (entry.duration_seconds || this.timePerEntry)
          : this.timePerEntry,
        notes: entry.notes || undefined
      }));

      if (batchData.length > 0) {
        await firstValueFrom(this.eventService.batchUpdateScheduleEntries({
          schedule_id: this.schedule.schedule_id,
          entries: batchData
        }));
      }

      this.hasChanges = false;
      this.activeModal.close(true);
    } catch (error) {
      console.error('Error saving start list:', error);
    } finally {
      this.saving = false;
    }
  }
}
